import { rolePlaySlotsFromSession, slotAssigneeId } from './rolePlay'
import { findPersonByKey, personUserId } from './pagePeopleModel'
import type {
  CommentLink,
  RoomChatPhase,
  ScenarioComment,
  ScenarioPerson,
  ScenarioSession,
} from './types'

/** http(s) URLs in plain text, for storage beside `body`. Trailing punctuation is stripped. */
export function commentLinksFromBody(body: string): CommentLink[] | undefined {
  const found = body.match(/https?:\/\/[^\s<>"']+/gi) ?? []
  const urls: string[] = []
  const seen = new Set<string>()
  for (const raw of found) {
    const trimmed = raw.replace(/[),.;:!?]+$/g, '')
    let href: string
    try {
      const parsed = new URL(trimmed)
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') continue
      href = parsed.href
    } catch {
      continue
    }
    if (seen.has(href)) continue
    seen.add(href)
    urls.push(href)
  }
  return urls.length ? urls.map((url) => ({ url })) : undefined
}

/** Live line with the audit fields filled. Optional future fields stay omitted until a feature writes them. */
export function liveComment(input: {
  id: string
  createdAt: string
  authorId?: string
  authorName?: string
  body: string
  kind?: 'message' | 'action'
  authorObjectId?: string
  /** Room task when posted (master thread). */
  roomPhase?: RoomChatPhase
  /** Official take id when posted during a live run. */
  runId?: string
}): ScenarioComment {
  const links = commentLinksFromBody(input.body)
  const name = input.authorName?.trim()
  return {
    id: input.id,
    createdAt: input.createdAt,
    createdAtWallIso: new Date().toISOString(),
    ...(input.authorId?.trim() ? { authorId: input.authorId.trim() } : {}),
    ...(name ? { authorName: name } : {}),
    ...(input.authorObjectId ? { authorObjectId: input.authorObjectId } : {}),
    body: input.body,
    ...(input.kind === 'action' ? { kind: 'action' as const } : {}),
    source: 'live',
    ...(input.roomPhase ? { roomPhase: input.roomPhase } : {}),
    ...(input.runId ? { runId: input.runId } : {}),
    ...(links ? { links } : {}),
  }
}

/**
 * First name + surname initial. Parenthetical job notes are not part of the name,
 * so "Eric Schwartz (developer…)" is "Eric S." rather than the last word's initial.
 */
export function abbreviatePersonName(name: string, lastName?: string): string {
  const stripped = name.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim()
  const parts = stripped.split(' ').filter(Boolean)
  const first = parts[0] || ''
  const explicit = lastName?.trim()
  if (!first && !explicit) return ''
  if (!first) return explicit!
  const surname = explicit || (parts.length > 1 ? parts[parts.length - 1]! : '')
  if (!surname || surname === first) return first
  if (/^\d+$/.test(surname)) return `${first} ${surname}`
  const initial = surname.replace(/[^A-Za-z]/g, '').charAt(0)
  if (!initial) return first
  return `${first} ${initial.toUpperCase()}.`
}

export type CommentWhoParts = { primary: string; hint: string }

export type CommentWhoOpts = {
  session?: ScenarioSession
  omitContactDetails?: boolean
  /**
   * exercise — seat/role primary, person as hint (radio / during-run).
   * session — person primary, seat as hint (Session facilitation).
   * Default exercise when unset (exports / legacy).
   */
  voice?: 'exercise' | 'session'
}

function seatLabelForObject(session: ScenarioSession, objectId: string): string | undefined {
  return rolePlaySlotsFromSession(session).find((s) => s.objectId === objectId)?.label
}

/**
 * Chat attribution: roleplay seat + person, ordered by `voice`.
 * No magic author sentinels — script voice is an object (+ optional assignee).
 */
export function commentWhoParts(
  comment: Pick<ScenarioComment, 'authorId' | 'authorObjectId' | 'source'>,
  people: readonly ScenarioPerson[],
  opts?: CommentWhoOpts,
): CommentWhoParts {
  if (comment.source === 'system') return { primary: 'System', hint: '' }

  const omit = Boolean(opts?.omitContactDetails)
  const voice = opts?.voice ?? 'exercise'
  const session = opts?.session
  const objectId = comment.authorObjectId?.trim()
  const authorId = comment.authorId?.trim()

  let seatLabel: string | undefined
  let person: ScenarioPerson | undefined

  if (session && objectId) {
    seatLabel = seatLabelForObject(session, objectId)
    if (session.rolePlayEnabled !== false) {
      const slot = rolePlaySlotsFromSession(session).find((s) => s.objectId === objectId)
      const assigneeId = slot ? slotAssigneeId(session, slot) : undefined
      if (assigneeId) person = findPersonByKey(people, assigneeId)
    }
  }

  if (!person && authorId) {
    person = findPersonByKey(people, authorId)
  }

  // Seat when the author holds a roleplay assignment — even if we already
  // resolved their person row (human chat often has authorId and no authorObjectId).
  if (!seatLabel && session && authorId && session.rolePlayEnabled !== false) {
    const seat = rolePlaySlotsFromSession(session).find((s) => {
      const aid = slotAssigneeId(session, s)
      if (!aid) return false
      return (
        aid === authorId ||
        (person != null && (aid === person.id || aid === personUserId(person)))
      )
    })
    if (seat) {
      seatLabel = seat.label
      if (!person) person = findPersonByKey(people, authorId)
    }
  }

  if (!seatLabel && session && objectId) {
    const obj = session.objects.find((o) => o.id === objectId)
    if (obj?.label.trim()) seatLabel = obj.label.trim()
  }

  const short = person
    ? abbreviatePersonName(person.name, person.lastName) || person.name.trim()
    : ''
  const fullName = person
    ? person.name.trim() || person.email || short || 'Participant'
    : ''

  if (voice === 'session') {
    if (person) {
      if (omit) return { primary: short || 'Participant', hint: seatLabel || '' }
      return { primary: fullName, hint: seatLabel || '' }
    }
    if (seatLabel) return { primary: seatLabel, hint: '' }
    if (comment.source === 'script') return { primary: 'Script', hint: '' }
    return { primary: authorId || objectId || 'Unknown', hint: '' }
  }

  if (seatLabel) {
    return { primary: seatLabel, hint: omit ? '' : short }
  }
  if (person) {
    if (omit) return { primary: short || 'Participant', hint: '' }
    return { primary: fullName, hint: '' }
  }
  if (comment.source === 'script') return { primary: 'Script', hint: '' }
  return { primary: authorId || objectId || 'Unknown', hint: '' }
}

/** Single-line attribution for exports and topic contributor lists. */
export function commentWho(
  comment: Pick<ScenarioComment, 'authorId' | 'authorObjectId' | 'source'>,
  people: readonly ScenarioPerson[],
  opts?: CommentWhoOpts,
): string {
  const { primary, hint } = commentWhoParts(comment, people, opts)
  return hint ? `${primary} (${hint})` : primary
}

export function commentIsAction(c: { kind?: string; body: string }): boolean {
  if (c.kind === 'action') return true
  // Legacy board notes: "{Name} placed …"
  return /^\S.+\s+placed\s+/i.test(c.body)
}

/** Typed room line (not script, system, or a board action emote). */
export function isLiveUserChatLine(c: Pick<ScenarioComment, 'kind' | 'source' | 'body'>): boolean {
  if (commentIsAction(c)) return false
  return c.source !== 'script' && c.source !== 'system'
}

/** Own live line, and the viewer still has an active roster seat. */
export function canManageOwnLiveComment(
  c: Pick<ScenarioComment, 'authorId' | 'kind' | 'source' | 'body'>,
  userId: string | null | undefined,
  onRoster: boolean,
): boolean {
  if (!onRoster || !isLiveUserChatLine(c)) return false
  const author = c.authorId?.trim() ?? ''
  const uid = userId?.trim() ?? ''
  return Boolean(author && uid && author.toLowerCase() === uid.toLowerCase())
}

/** Current text stays in `body`; the previous sentence is appended to `revisions[]`. */
export function reviseLiveComment(c: ScenarioComment, nextBody: string): ScenarioComment {
  const body = nextBody.trim()
  if (!body || body === c.body) return c
  const links = commentLinksFromBody(body)
  const next: ScenarioComment = {
    ...c,
    body,
    revisions: [...(c.revisions ?? []), { atWallIso: new Date().toISOString(), body: c.body }],
  }
  if (links) next.links = links
  else delete next.links
  return next
}

/** Hide in Chat / AAR; keep the row (and `body`) in SessionJson. */
export function withdrawLiveComment(c: ScenarioComment, removedById: string): ScenarioComment {
  return {
    ...c,
    removedAtWallIso: new Date().toISOString(),
    removedById: removedById.trim(),
  }
}

/** Strip duplicated author from legacy “Name placed …” action bodies. */
export function actionCommentBody(body: string): string {
  const m = body.match(/^.+?\s+(placed\s+.+)$/i)
  const rest = (m?.[1] ?? body).trim()
  if (!rest) return body.trim()
  return rest.charAt(0).toUpperCase() + rest.slice(1)
}

/**
 * Comments pane body: drop a leading “Who: ” that duplicates the attribution line
 * (common in radio-style canned traffic). Keep other prefixes like “911: …”.
 */
export function commentThreadBody(body: string, who: string, action: boolean): string {
  if (action) return actionCommentBody(body)
  const raw = body.trim()
  const label = who.trim()
  if (!label) return raw
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const stripped = raw.replace(new RegExp(`^${escaped}:\\s*`, 'i'), '').trim()
  return stripped || raw
}
